import {
	CampaignNominationSchemeRatioPercentage,
	FileSystemCampaignDefinitionProvider,
} from "./Campaign/CampaignDefinitionProvider";
import {
	DynamicUxClientState,
	FloodgateStorageProviderFileType as FileType,
	GovernanceServiceClientState,
	GovernedChannelType,
	IActivityListener,
	IActivityTrackingSet,
	IAppGovernanceProvider,
	ICampaignDefinition,
	ICampaignDefinitionProvider,
	ICampaignStateProvider,
	ICampaignTelemetrySamplingConfig,
	IContextData,
	IDictionary,
	IFloodgateEnvironmentProvider,
	IFloodgateSettingStorageCallback,
	IFloodgateStorageProvider,
	IFloodgateStringProvider,
	IFloodgateTelemetryLogger,
	IGovernanceParameters,
	IGovernanceServiceClient,
	IGovernanceServiceResult,
	ILogExperimentTrigger,
	IOnSurveyActivatedCallback,
	IPersonalizerClient,
	ISurvey,
	ISurveyCandidacyChecker,
	ISurveyForm,
	ISurveyInfo,
	ISurveyLauncher,
	ISurveyLauncherFactory,
	ITelemetryProperties,
	ITransporterFactory,
	SurveyType,
	TreatmentType,
} from "@ms-ofb/floodgate-shared-typings";
import { GovernedChannelStateProviderFactory, IGovernedChannelStateProvider } from "./GovernedChannelStateProvider";
import {
	ISurveyActivationStatsProvider,
	SurveyActivationStats,
	SurveyActivationStatsProviderFactory,
	SurveyStatCollectionActivation,
} from "./SurveyStatCollectionActivation";
import { SurveyEventActivityStats, SurveyStatCollectionEventActivity } from "./SurveyStatCollectionEventActivity";
import {
	hasSurveyReachedMaxLaunches,
	hasSurveyReachedMaxLaunchesPerSession,
	isBusinessCriticalSurvey,
	isCriticalSurvey,
	isMultiLaunchSurvey
} from "./GovernanceUtils";

import { ActivityTrackingContract } from "./ActivityTrackingContract";
import { CampaignManager } from "./Campaign/CampaignManager";
import { CampaignStateProviderFactory } from "./Campaign/CampaignStateProvider";
import { FloodgateSettings } from "./FloodgateSettings";
import { IUserFactProvider } from "./Api/IUserFactProvider";
import SurveyActivityListener from "./SurveyActivityListener";
import { TelemetryEvent } from "./Constants";
import { isNOU } from "./Utils";
import { IFloodgateEngineConfig } from "./Api/IFloodgateEngineConfig";
import FloodgateSurveyCdnClient, { isTranslationRequired } from "./FloodgateSurveyCdn/FloodgateSurveyCdnClient";
import { getFeatureGateProvider } from "./FeatureGateProvider";

import { CampaignBusinessLevel, GovernanceDecision, IGovernanceDecision, IOrchestrationEngine } from "./OrchestrationEngine/Api";
import { OrchestrationEngine } from "./OrchestrationEngine/OrchestrationEngine";
import { TelemetryLogger } from "./TelemetryLogger";

export const enum InitializationStatus {
	None = 0,
	Error,
	Initializing,
	Started,
	Stopped,
}

const defaultSurveyActivityListener: SurveyActivityListener.IActivityListenerCallback = {
	run(survey) {
	},
	shouldAcceptActivity(activityName) {
		return true;
	},
};

const globalSessionSurveyActivationCounts: IDictionary<number> = {};

/**
 * Internal interface is used for FloodgateEngine calss only.
 * It hold the neccessary information for stale activated campaign which is waiting for its turn
 * to call to Governance Service, due to the previous call is still in progress
 */
interface IStaleTriggeredSurvey {
	survey: ISurvey,
	contextInfo: IContextData[]
}

/**
 * Floodgate Engine class.  The main wiring hub and the brains responsible for
 * linking together the survey definitions, governance, activity tracking, and
 * survey launchers.  Only one-instance-at-a-time of these should be created
 * per app session.
 */
export class FloodgateEngine {
	public static getTransportFactory(): ITransporterFactory {
		return FloodgateEngine.transporterFactory;
	}

	public static make(
		buildVersion: string,
		launcherFactory: ISurveyLauncherFactory,
		onSurveyActivatedCallback: IOnSurveyActivatedCallback,
		fileBasedStorageProvider: IFloodgateStorageProvider,
		hostBasedStorageProvider: IFloodgateSettingStorageCallback,
		stringProvider: IFloodgateStringProvider,
		environmentProvider: IFloodgateEnvironmentProvider,
		transporterFactory: ITransporterFactory,
		campaignDefinitionProviders: ICampaignDefinitionProvider[],
		userFactsProvider: IUserFactProvider,
		governanceServiceClient: IGovernanceServiceClient,
		isPriorityEnabled: boolean,
		floodgateEngineConfig: IFloodgateEngineConfig,
		appGovernanceProvider?: IAppGovernanceProvider,
		logExperimentTrigger?: ILogExperimentTrigger, 
		campaignTelemetrySamplingConfig?: ICampaignTelemetrySamplingConfig,
		personalizerClient?: IPersonalizerClient,
		messagingEnabled?: boolean,
		surveyEnabled?: boolean,
		surveyClient?: CampaignManager): FloodgateEngine {

		try {
			const governedChannelStateProvider: IGovernedChannelStateProvider
				= GovernedChannelStateProviderFactory.make(fileBasedStorageProvider, hostBasedStorageProvider);

			const surveyActivationStatsProvider: ISurveyActivationStatsProvider
				= SurveyActivationStatsProviderFactory.make(fileBasedStorageProvider, hostBasedStorageProvider);

			const campaignStateProvider: ICampaignStateProvider
				= CampaignStateProviderFactory.make(fileBasedStorageProvider, hostBasedStorageProvider);

			// Push to front of array so that it is the first provider to be checked.
			// FilterCampaignData method overwrites the latest campaign definition with the same id.
			// So, the static campaign definition provider should have the least priority during override.
			campaignDefinitionProviders.unshift(new FileSystemCampaignDefinitionProvider(fileBasedStorageProvider));

			const orchestrationEngine = new OrchestrationEngine(governanceServiceClient, governedChannelStateProvider, appGovernanceProvider );

			const getSurveyCandidacyChecker = (surveyForm: ISurveyForm) => {
				return orchestrationEngine.isSurveyTrackable(surveyForm);
			};

			const surveyCandidacyChecker: ISurveyCandidacyChecker = { isSurveyTrackable: getSurveyCandidacyChecker };

			const client = surveyClient || new CampaignManager(
				campaignStateProvider,
				campaignDefinitionProviders,
				stringProvider,
				environmentProvider,
				buildVersion,
				new Date(),
				isPriorityEnabled,
				userFactsProvider,
				surveyCandidacyChecker,
				logExperimentTrigger,
				campaignTelemetrySamplingConfig,
				messagingEnabled,
				surveyEnabled,
			);

			return new FloodgateEngine(
				client,
				new SurveyActivityListener(
					defaultSurveyActivityListener,
					TelemetryLogger.getTelemetryLogger,
				),
				launcherFactory,
				onSurveyActivatedCallback,
				fileBasedStorageProvider,
				orchestrationEngine,
				surveyActivationStatsProvider,
				transporterFactory,
				campaignTelemetrySamplingConfig,
				floodgateEngineConfig,
				personalizerClient
			);
		} catch (e) {
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.Make.Failed,
				{ ErrorMessage: e && e.toString(), ErrorDetails: e && e.stack });
		}
	}

	private static transporterFactory: ITransporterFactory;

	private surveyClient: CampaignManager;
	private activityListener: SurveyActivityListener;
	private onSurveyActivatedCallback: IOnSurveyActivatedCallback;
	private launcherFactory: ISurveyLauncherFactory;
	private storage: IFloodgateStorageProvider;
	private orchestrationEngine: IOrchestrationEngine;
	private initializationStatus: InitializationStatus = InitializationStatus.None;
	private loggedFirstStart: boolean;

	private floodgateSettings: FloodgateSettings;
	private previousSurveyActivationStats: SurveyStatCollectionActivation;
	private previousSurveyEventActivityStats: SurveyStatCollectionEventActivity;

	private candidateSurveys: IDictionary<ISurvey> = {};
	private launchedSurveys: IDictionary<ISurvey> = {};
	private launchedLaunchers: ISurveyLauncher[] = [];
	private surveyEventSent: string[] = [];

	private surveyActivationStatsProvider: ISurveyActivationStatsProvider;

	private staleTriggeredSurveysQueue: IStaleTriggeredSurvey[] = [];
	private campaignTelemetrySamplingConfig: ICampaignTelemetrySamplingConfig;
	private floodgateEngineConfig: IFloodgateEngineConfig;

	private personalizerClient: IPersonalizerClient;

	public constructor(surveyClient: CampaignManager, activityListener: SurveyActivityListener, launcherFactory: ISurveyLauncherFactory,
		onSurveyActivatedCallback: IOnSurveyActivatedCallback, storage: IFloodgateStorageProvider, orchestrationEngine: IOrchestrationEngine,
		surveyActivationStatsProvider: ISurveyActivationStatsProvider, transporterFactory: ITransporterFactory,
		campaignTelemetrySamplingConfig: ICampaignTelemetrySamplingConfig,
		floodgateEngineConfig: IFloodgateEngineConfig, personalizerClient: IPersonalizerClient) {

		if (!surveyClient) {
			throw new Error("surveyClient must not be null");
		}
		if (!activityListener) {
			throw new Error("activityListener must not be null");
		}
		if (!launcherFactory) {
			throw new Error("launcherFactory must not be null");
		}
		if (!onSurveyActivatedCallback) {
			throw new Error("onSurveyActivatedCallback must not be null");
		}
		if (!storage) {
			throw new Error("storage must not be null");
		}
		if (!orchestrationEngine) {
			throw new Error("governor must not be null");
		}
		if (!transporterFactory) {
			throw new Error("transporterFactory must not be null");
		}

		this.surveyClient = surveyClient;
		this.activityListener = activityListener;
		this.launcherFactory = launcherFactory;
		this.onSurveyActivatedCallback = onSurveyActivatedCallback;
		this.storage = storage;
		this.orchestrationEngine = orchestrationEngine;
		this.surveyActivationStatsProvider = surveyActivationStatsProvider;
		this.campaignTelemetrySamplingConfig = campaignTelemetrySamplingConfig;
		this.floodgateEngineConfig = floodgateEngineConfig;
		this.personalizerClient = personalizerClient;
		FloodgateEngine.transporterFactory = transporterFactory;

		this.loggedFirstStart = false;

		// Initialize our list to a valid, empty collection
		this.setPendingSurveys(null);

		// If the listener was pre-configured with survey for some reason, clear it now
		this.activityListener.clearSurveys();

		// Link ourselves with the listener
		/* eslint-disable-next-line @typescript-eslint/no-this-alias*/
		const thisObj = this; // Save current object in a variable to allow access from callback.
		this.activityListener.setCallback({
			run(surveyId: string, contextInfo: IContextData[]) {
				thisObj.onSurveyActivated(surveyId, contextInfo);
			},
			shouldAcceptActivity(activityName: string) {
				return thisObj.shouldAcceptActivity(activityName);
			},
		});
	}

	public getSurveyClient(): CampaignManager {
		return this.surveyClient;
	}

	/**
	 * Save all internal stats and floodgateSettings (merging with existing file contents), without stopping the engine.
	 */
	public mergeAndSave(): void {
		this.saveSettings();
		this.saveSurveyActivationHistory();
		this.saveSurveyEventActivityHistory();

		// Inform surveyClient to save its state
		this.surveyClient.saveCurrentState();

		// Save governed channel states
		this.orchestrationEngine.mergeAndSave();
	}

	/**
	 * Start the engine.  Checks feature enable state, causes survey definitions to be read, and tracked activities to be set on the listener.
	 */
	public start(): void {
		if (this.initializationStatus === InitializationStatus.Initializing ||
			this.initializationStatus === InitializationStatus.Started) {
			return;
		}

		try {
			this.setInitializationStatus(InitializationStatus.Initializing);
			this.loadSettingsAndPriorEventCounts();

			const channelTypes: GovernedChannelType[] = this.orchestrationEngine.getAvailableChannels();

			this.surveyClient.refreshSurveyDefinitions(channelTypes);

			this.setPendingSurveysAndStartFloodgate();
			this.setInitializationStatus(InitializationStatus.Started);
		} catch (e) {
			this.setInitializationStatus(InitializationStatus.Error);
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.Start.Failed,
				{ ErrorMessage: e && e.toString(), ErrorDetails: e && e.stack });
		}
	}

	/**
	 * Start the engine.  Checks feature enable state, causes survey definitions to be read, and tracked activities to be set on the listener.
	 */
	public startAsync(): Promise<void> {
		if (this.initializationStatus === InitializationStatus.Initializing ||
			this.initializationStatus === InitializationStatus.Started) {
			return Promise.resolve();
		}

		try {
			this.setInitializationStatus(InitializationStatus.Initializing);
			this.loadSettingsAndPriorEventCounts();

			const channelTypes: GovernedChannelType[] = this.orchestrationEngine.getAvailableChannels();

			return new Promise((resolve, reject) => {
				this.surveyClient.refreshSurveyDefinitionsAsync(channelTypes).then(
					() => {
						// Handle any stop() calls that happened while initializing
						if (this.initializationStatus === InitializationStatus.Stopped) {
							this.setInitializationStatus(InitializationStatus.Started);
							this.stop();
							TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.StartAsync.Stopped,
								{ ErrorMessage: "Stopped because of a pending stop() call" });
						} else {
							this.setPendingSurveysAndStartFloodgate();
						}

						resolve();
					},
				).catch(
					(error: Error) => {
						this.setInitializationStatus(InitializationStatus.Error);
						TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.StartAsync.Failed,
							{ ErrorMessage: "Survey refresh error " + (error && error.message) });

						reject(error);
					},
				);
			});
		} catch (e) {
			this.setInitializationStatus(InitializationStatus.Error);
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.StartAsync.Failed,
				{ ErrorMessage: e && e.toString(), ErrorDetails: e && e.stack });
			return Promise.resolve();
		}
	}

	/**
	 * Stop the engine.  Causes tracked activities to be cleared and any in-progress counters not otherwise saved to be thrown out.
	 */
	public stop(): void {
		if (this.initializationStatus === InitializationStatus.Started) {
			this.mergeAndSave();

			// Clear our Survey list, and push that through to the listener
			this.setPendingSurveys(null);
			this.updateActivityListenerWithCurrentSurveyDefinitions();
		}

		this.setInitializationStatus(InitializationStatus.Stopped);
	}

	/**
	 * Gets the IActivityListener logging interface for callers that want to log directly rather than through telemetry
	 */
	public getActivityListener(): IActivityListener {
		return this.activityListener;
	}

	private setInitializationStatus(status: InitializationStatus): void {
		this.initializationStatus = status;
	}

	private saveSettings(): void {
		this.storage.fileLock(FileType.FloodgateSettings);

		try {
			this.writeString(FileType.FloodgateSettings, FloodgateSettings.toJson(this.floodgateSettings));
		} finally {
			this.storage.fileUnlock(FileType.FloodgateSettings);
		}
	}

	private loadSettingsAndPriorEventCounts(): void {
		try {
			// Load up the prior survey history and prior event counts
			this.previousSurveyActivationStats = this.surveyActivationStatsProvider.load();

			this.previousSurveyEventActivityStats =
				SurveyStatCollectionEventActivity.fromJson(this.readString(FileType.SurveyEventActivityStats));

			// get the list of survey Id - all the survey should have event sent already
			const stats: IDictionary<SurveyEventActivityStats> = this.previousSurveyEventActivityStats.getStats();
			for (const key in stats) {
				if (Object.prototype.hasOwnProperty.call(stats, key)) {
					this.surveyEventSent.push(key);
				}
			}

			// Load up the general floodgateSettings
			this.floodgateSettings = FloodgateSettings.fromJson(this.readString(FileType.FloodgateSettings));
		} catch (e) {
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.Common.Error,
				{ ErrorMessage: "Error loading prior settings/events " + (e && e.toString()) });
		}
	}

	private setPendingSurveysAndStartFloodgate(): void {
		// Update our survey list, and push that through to the listener
		this.setPendingSurveys(this.surveyClient.getAppSurveys());
		this.updateActivityListenerWithCurrentSurveyDefinitions();

		// Mark us as started
		this.setInitializationStatus(InitializationStatus.Started);

		// Log our first start (aka floodgate boot) event
		if (!this.loggedFirstStart) {
			this.loggedFirstStart = true;
			this.getActivityListener().logActivity(SurveyActivityListener.FloodgateStartActivityName);
		}
	}

	private updateSurveyActivationStats(survey: ISurvey) {
		const surveyInfo = survey.getSurveyInfo();
		const surveyId = surveyInfo.getId();
		const stats = this.previousSurveyActivationStats.getStats()[surveyId] || new SurveyActivationStats();
		stats.Type = survey.getType();
		stats.ExpirationTimeUtc = surveyInfo.getExpirationTimeUtc();
		stats.ActivationTimeUtc = new Date();
		stats.SurveyLaunchCount++;

		this.previousSurveyActivationStats.addStats(surveyId, stats);

		// Save the SurveyLaunchCountPerSession into global memory but not local storage
		// so it won't be overwritten in local storage by another app instance
		if (!Object.prototype.hasOwnProperty.call(globalSessionSurveyActivationCounts, surveyId)) {
			globalSessionSurveyActivationCounts[surveyId] = 0;
		}
		globalSessionSurveyActivationCounts[surveyId]++;
	}

	private saveSurveyActivationHistory(): void {
		this.storage.fileLock(FileType.SurveyActivationStats);

		try {
			const statCollection: SurveyStatCollectionActivation = this.surveyActivationStatsProvider.load();

			const previousStats = this.previousSurveyActivationStats.getStats();

			// Build the update collection
			const updateCollection = new SurveyStatCollectionActivation();
			for (const key in this.launchedSurveys) {
				if (Object.prototype.hasOwnProperty.call(this.launchedSurveys, key)) {
					const survey: ISurvey = this.launchedSurveys[key];
					const surveyId = survey.getSurveyInfo().getId();

					// Make this part of the update list
					const stats = previousStats[surveyId];
					updateCollection.addStats(surveyId, stats);
				}
			}

			// Actually merge our updates into the full collection
			statCollection.accumulate(updateCollection);
			this.surveyActivationStatsProvider.save(statCollection);

			// Make sure to keep our internal collection consistent with what we just wrote
			this.previousSurveyActivationStats = statCollection;
		} finally {
			this.storage.fileUnlock(FileType.SurveyActivationStats);
		}
	}

	private saveSurveyEventActivityHistory(): void {
		this.storage.fileLock(FileType.SurveyEventActivityStats);

		try {
			const statCollection: SurveyStatCollectionEventActivity =
				SurveyStatCollectionEventActivity.fromJson(this.readString(FileType.SurveyEventActivityStats));

			const now = new Date();
			// Fill out our list of updates
			const updateCollection = new SurveyStatCollectionEventActivity();
			for (const key in this.candidateSurveys) {
				if (Object.prototype.hasOwnProperty.call(this.candidateSurveys, key)) {
					const stats: SurveyEventActivityStats = new SurveyEventActivityStats();
					const survey: ISurvey = this.candidateSurveys[key];

					if (!survey.getSurveyInfo().isActiveForDate(now)) {
						continue;
					}

					stats.ExpirationTimeUtc = survey.getSurveyInfo().getExpirationTimeUtc();

					// Get the activities which are aggregated, if there aren't any continue
					const allActivities: IActivityTrackingSet = survey.getSurveyInfo().getActivationEvent().getTrackingSet();
					const aggregateActivities: string[] = [];
					const nonAggregateActivities: string[] = [];

					allActivities.getList().forEach((data) => {
						if (data) {
							if (data.getIsAggregate()) {
								aggregateActivities.push(data.getActivity());
							} else {
								nonAggregateActivities.push(data.getActivity());
							}
						}
					});

					stats.Counts = new Array(aggregateActivities.length);

					// Save off the counts we've added for this session
					for (let i = 0; i < aggregateActivities.length; i++) {
						stats.Counts[i] = this.activityListener.moveSessionCountIntoBaseCount(aggregateActivities[i], survey.getSurveyInfo().getId());
					}

					for (const activity of nonAggregateActivities) {
						this.activityListener.saveSessionTrackingActivity(activity);
					}

					// Make this part of the update list
					updateCollection.addStats(survey.getSurveyInfo().getId(), stats);
				}
			}

			// Actually merge our updates into the full collection
			statCollection.accumulate(updateCollection);

			this.writeString(FileType.SurveyEventActivityStats, SurveyStatCollectionEventActivity.toJson(statCollection));
			// Make sure to keep our internal collection consistent with what we just wrote
			this.previousSurveyEventActivityStats = statCollection;
		} finally {
			this.storage.fileUnlock(FileType.SurveyEventActivityStats);
		}
	}

	private setPendingSurveys(pendingSurveys: IDictionary<ISurvey>): void {
		this.candidateSurveys = {};
		if (pendingSurveys) {
			const rejectCampaigns = [];
			const acceptCampaigns = [];
			for (const key in pendingSurveys) {
				if (Object.prototype.hasOwnProperty.call(pendingSurveys, key)) {
					const survey: ISurvey = pendingSurveys[key];
					const payload: Record<string, unknown> = {
						CampaignId: survey.getCampaignId(),
						SurveyId: key
					}
					if (this.launcherFactory.AcceptsSurvey(survey)) {
						this.candidateSurveys[key] = survey;
						acceptCampaigns.push(payload);
					}
					else {
						rejectCampaigns.push(payload);
					}
				}
			}

			// Default sampling rate: 0% - it's turned off by default. We will enable it from the Host app side if needed.
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.Common.ActiveSurveys,
				{ Message: JSON.stringify(
					{
						RejectCampaigns: rejectCampaigns,
						CandidateCampaigns: acceptCampaigns,
						PreviousCandidateSurveyIds: this.surveyEventSent
					})
				}, 0);
		}
	}

	private getCurrentSurveyDefinitions(): ISurvey[] {
		// Get a vector of Surveys from our id-based map
		const surveyList: ISurvey[] = new Array<ISurvey>();

		const allCampaignDefinitionIDs = this.getAllCampaignDefinitionIDs();
		const allCandidateCampaignDefinitionIDs = this.getAllCandidateCampaignDefinitionIDs();

		// Get all the campaign definitions. We need info from these for telemetry.
		const allCampaignDefinitions = (this.surveyClient as CampaignManager).getCampaignDefinitions();

		const isSurveyAllowed = (surveyInfo: ISurveyInfo) => {
			// always allow business critical and approved campaigns
			if (isBusinessCriticalSurvey(surveyInfo)) {
				return true;
			}

			const surveyActivationStats = this.previousSurveyActivationStats.getBySurveyId(surveyInfo.getId());
			if (isMultiLaunchSurvey(surveyInfo) &&
				!hasSurveyReachedMaxLaunches(surveyInfo, surveyActivationStats) &&
				!hasSurveyReachedMaxLaunchesPerSession(surveyInfo, globalSessionSurveyActivationCounts)) {
				return true;
			}

			// Skip over any candidate surveys that have been previously completed or not active anymore
			if (surveyActivationStats || !surveyInfo.isActiveForDate(new Date())) {
				return false;
			}

			return true;
		};

		for (const key in this.candidateSurveys) {
			if (Object.prototype.hasOwnProperty.call(this.candidateSurveys, key)) {
				const survey: ISurvey = this.candidateSurveys[key];
				const surveyInfo = survey.getSurveyInfo();
				const campaignDefinition: ICampaignDefinition = allCampaignDefinitions[surveyInfo?.getBackEndId()];
				if (!campaignDefinition || !isSurveyAllowed(surveyInfo)) {
					continue;
				}

				// send the UserSelected event if it is not sent before
				if (this.surveyEventSent.indexOf(key) < 0) {
					this.surveyEventSent.push(key);

					const additionalSurveyInfo = this.getAdditionalSurveyInfoForTelemetry(survey,
						null /* contextInfo */,
						allCampaignDefinitionIDs,
						allCandidateCampaignDefinitionIDs);

					TelemetryLogger.getTelemetryLogger().log_UserSelected(surveyInfo.getBackEndId(),
						surveyInfo.getId(),
						survey.getType(),
						JSON.stringify(additionalSurveyInfo),
						surveyInfo.getTreatmentType());
				}

				surveyList.push(survey);
			}
		}

		return surveyList;
	}

	private getAdditionalSurveyInfoForTelemetry(survey: ISurvey,
		contextInfo: IContextData[], allCampaignDefinitionIDs: string[], allCandidateCampaignDefinitionIDs: string[]): {
			allCandidateCampaignDefinitionIDs: string[],
			allCampaignDefinitionIDs: string[],
			contextCount?: number,
			surveyLauncherType: string,
			channelType: GovernedChannelType,
			percentageNumerator: number,
			percentageDenominator: number,
			nominationPeriodNumber: number,
			cooldownPeriodNumber: number,
			businessLevel: number,
			maxLaunches: number,
			maxLaunchesPerSession: number,
			governanceServiceClientState?: GovernanceServiceClientState,
			isContentValid: boolean,
			isUxSchemaPresent: boolean,
		} {
		// Get all the campaign definitions. We need info from these for telemetry.
		const allCampaignDefinitions = this.surveyClient.getCampaignDefinitions();

		// Get the corresponding campaign definition to the survey in question.
		const correspondingCampaignDefinition = allCampaignDefinitions[survey.getCampaignId()];

		// Survey properties to get from its corresponding CampaignDefinition:
		let nominationPeriodNumber: number;
		let cooldownPeriodNumber: number;

		const percentageNumerator = (correspondingCampaignDefinition.nominationScheme as CampaignNominationSchemeRatioPercentage).percentageNumerator;
		const percentageDenominator = (correspondingCampaignDefinition.nominationScheme as CampaignNominationSchemeRatioPercentage).percentageDenominator;

		nominationPeriodNumber = correspondingCampaignDefinition.nominationScheme.nominationPeriod.asTimeIntervalSeconds();
		nominationPeriodNumber = isNOU(nominationPeriodNumber) ? -1 : nominationPeriodNumber; // Setting to -1 if null or undefined

		cooldownPeriodNumber = correspondingCampaignDefinition.nominationScheme.cooldownPeriod.asTimeIntervalSeconds();
		cooldownPeriodNumber = isNOU(cooldownPeriodNumber) ? -1 : cooldownPeriodNumber;  // Setting to -1 if null or undefined

		const surveyInfo = survey.getSurveyInfo();
		return {
			allCampaignDefinitionIDs,
			allCandidateCampaignDefinitionIDs,
			channelType: surveyInfo.getGovernedChannelType(),
			contextCount: contextInfo ? contextInfo.length : undefined,
			cooldownPeriodNumber,
			nominationPeriodNumber,
			percentageDenominator,
			percentageNumerator,
			surveyLauncherType: survey.getLauncherType(),
			businessLevel: surveyInfo.getBusinessLevel(),
			maxLaunches: surveyInfo.getMaxLaunches(),
			maxLaunchesPerSession: surveyInfo.getMaxLaunchesPerSession(),
			isContentValid: survey.isContentValid(),
			isUxSchemaPresent: !isNOU(survey.getUxSchema())
		};
	}

	private getAllCandidateCampaignDefinitionIDs(): string[] {

		const allCandidateCampaignDefinitionIDs: string[] = [];

		// Get all the candidate surveys' campaign definition IDs
		for (const key in this.candidateSurveys) {
			if (Object.prototype.hasOwnProperty.call(this.candidateSurveys, key)) {
				const candidateSurvey: ISurvey = this.candidateSurveys[key];
				allCandidateCampaignDefinitionIDs.push(candidateSurvey.getSurveyInfo().getBackEndId());
			}
		}
		return allCandidateCampaignDefinitionIDs;
	}

	private getAllCampaignDefinitionIDs(): string[] {

		const allCampaignDefinitions = this.surveyClient.getCampaignDefinitions();
		const allCampaignDefinitionIDs: string[] = [];

		for (const definitionsKey in allCampaignDefinitions) {
			if (Object.prototype.hasOwnProperty.call(allCampaignDefinitions, definitionsKey)) {
				const definition: ICampaignDefinition = allCampaignDefinitions[definitionsKey];
				allCampaignDefinitionIDs.push(definition.campaignId);
			}
		}
		return allCampaignDefinitionIDs;
	}

	private updateActivityListenerWithCurrentSurveyDefinitions(): void {
		const surveyList: ISurvey[] = this.getCurrentSurveyDefinitions();
		const trackingContractList: ActivityTrackingContract[] = [];

		for (const survey of surveyList) {
			const surveyInfo = survey.getSurveyInfo();
			const trackingContract = new ActivityTrackingContract(surveyInfo.getId(), surveyInfo.getActivationEvent().getTrackingSet());
			trackingContractList.push(trackingContract);
		}

		this.activityListener.SetActivityTrackingContracts(trackingContractList, this.previousSurveyEventActivityStats);
	}

	private setCriticalPendingSurveysAndUpdateActivityListener() {
		const activeSurveys: IDictionary<ISurvey> = {};

		// Get all the candidate surveys' campaign definition IDs
		for (const key in this.candidateSurveys) {
			if (Object.prototype.hasOwnProperty.call(this.candidateSurveys, key)) {
				const candidateSurvey: ISurvey = this.candidateSurveys[key];
				if (isCriticalSurvey(candidateSurvey.getSurveyInfo())) {
					activeSurveys[key] = candidateSurvey;
				}
			}
		}

		if (Object.keys(activeSurveys).length > 0) {
			this.setPendingSurveys(activeSurveys);
			this.updateActivityListenerWithCurrentSurveyDefinitions();
		}
	}

	private shouldAcceptActivity(activityName: string): boolean {
		return this.initializationStatus === InitializationStatus.Started ||
			activityName === SurveyActivityListener.FloodgateStartActivityName;
	}

	private onSurveyActivated(surveyId: string, contextInfo: IContextData[]): void {
		// Find the survey object that corresponds to the surveyId passed
		const surveys: ISurvey[] = this.getCurrentSurveyDefinitions();
		let survey: ISurvey;

		for (const tempSurvey of surveys) {
			if (tempSurvey.getSurveyInfo().getId() === surveyId) {
				survey = tempSurvey;
				break;
			}
		}

		if (isNOU(survey)) {
			TelemetryLogger.getTelemetryLogger().log_Error(
				TelemetryEvent.FloodgateEngine.OnSurveyActivated.SurveyNotDefined,
				"Survey is not defined in the current survey definitions.",
			);
			return;
		}

		try {
			// this log will trigger when the user has first met the trigger action required to initially trigger the campaign.
			// this log occurs prior to the post governance checks which determines if the trigger is actually met and we can launch 
			// the surface
			// Default sampling rate: 10%
			const initialTriggerSamplingRate = this.campaignTelemetrySamplingConfig?.initialTriggerMetSamplingRate 
				?? 0.1;
			if (initialTriggerSamplingRate > 0) {
				TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.CampaignInitialTriggerMet, 
					{CampaignId: survey.getCampaignId(), Message: "Sampling Rate: " + initialTriggerSamplingRate}, initialTriggerSamplingRate);
			}

			const governedChannelType: GovernedChannelType = survey.getSurveyInfo().getGovernedChannelType();

			// If the governance service is in use, launch survey asynchronously
			if (this.orchestrationEngine.isChannelGovernanceEnabled(governedChannelType) ||
				(this.floodgateEngineConfig.uiStringsCdnEnabled && isTranslationRequired(survey, this.floodgateEngineConfig.locale))) {
				// record the stale surveys, so we can clear its tracker if Engine is stopped
				this.staleTriggeredSurveysQueue.push({
					survey: survey,
					contextInfo: contextInfo
				});

				// If there is no survey in stale before, launch the survey right away
				if (this.staleTriggeredSurveysQueue.length === 1) {
					// Launch the survey asynchronously
					this.checkTriggerMetApisAndActivateSurvey(survey, contextInfo);
				}
			} else {
				this.activateSurvey(survey, contextInfo);
			}
		} catch (e) {
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.OnSurveyActivated.Error,
				{ ErrorMessage: `Error activating survey ${e && e.toString()}`, ErrorDetails: e && e.stack });
		}
	}

	private activateSurvey(survey: ISurvey, contextInfo: IContextData[]) {
		let launchSurvey = false;
		const governedChannelType = survey.getSurveyInfo().getGovernedChannelType();

		// Figure out if the activated survey is still relevant
		if (!this.candidateSurveys[survey.getSurveyInfo().getId()]) {
			// Survey is no longer relevant but was activated. Suppress it.
			TelemetryLogger.getTelemetryLogger().log_Event(
				TelemetryEvent.FloodgateEngine.OnSurveyActivated.ActivationIrrelevantSurvey,
				{
					CampaignId: survey.getSurveyInfo().getBackEndId(),
					SurveyId: survey.getSurveyInfo().getId(),
				});
		} else if (!survey.getSurveyInfo().isActiveForDate(new Date())) {
			// Survey is no longer active (e.g. it was when we registered it but it has now expired)
			TelemetryLogger.getTelemetryLogger().log_Event(
				TelemetryEvent.FloodgateEngine.OnSurveyActivated.ActivationExpiredSurvey,
				{
					CampaignId: survey.getSurveyInfo().getBackEndId(),
					SurveyId: survey.getSurveyInfo().getId(),
				});
		} else {
			if (isCriticalSurvey(survey.getSurveyInfo())) {
				launchSurvey = true;
			} else if (!this.orchestrationEngine.isChannelOpen(governedChannelType)) {
				// Channel has closed and maxLaunches is not set, suppress the survey
				TelemetryLogger.getTelemetryLogger().log_Event(
					TelemetryEvent.FloodgateEngine.OnSurveyActivated.ClosedChannelType,
					{ ClosedChannelType: GovernedChannelType[governedChannelType] });
			} else {
				// Get latest survey states from provider
				const refreshedSurveyActivationStats = this.surveyActivationStatsProvider.load();
				if (refreshedSurveyActivationStats.getBySurveyId(survey.getSurveyInfo().getId())) {
					// Survey stats exists already, don't launch
					TelemetryLogger.getTelemetryLogger().log_Event(
						TelemetryEvent.FloodgateEngine.OnSurveyActivated.ActivationStatsSuppressedSurvey,
						{
							CampaignId: survey.getSurveyInfo().getBackEndId(),
							SurveyId: survey.getSurveyInfo().getId(),
						});
				} else if (Object.keys(this.launchedSurveys).length === 0) {
					// for now we only support launching one survey per session unless it is multi-launch campaign
					// Track this survey activation for the launch history tracker
					launchSurvey = true;
				} else {
					const triggeredSurvey = Object.values(this.launchedSurveys)[0];
					if (!isNOU(triggeredSurvey)) {
						TelemetryLogger.getTelemetryLogger().log_Event(
							TelemetryEvent.FloodgateEngine.OnSurveyActivated.SessionSuppressedSurvey,
							{
								CampaignId: triggeredSurvey.getSurveyInfo().getBackEndId(),
								SurveyId: triggeredSurvey.getSurveyInfo().getId(),
							});
					}
				}
			}

			if (launchSurvey) {
				this.launchedSurveys[survey.getSurveyInfo().getId()] = survey;
			}
		}

		// At this point, regardless of whether or not the survey is still relevant, we should flush out the stats
		this.updateSurveyActivationStats(survey);
		this.mergeAndSave();
		// shut off further survey launches
		this.activityListener.clearSurveys();

		// Clear the survey from stale list
		this.staleTriggeredSurveysQueue = [];

		// Actually launch the survey if possible
		if (launchSurvey) {
			// Get the stringified json with activity names, counts and isAggregate info for TriggetMet telemetry.
			const activityTrackingString = survey.getSurveyInfo().getActivationEvent().getTrackingSet()
				.getActivityTrackingInfo(this.activityListener, survey.getSurveyInfo().getId());

			const additionalSurveyInfo = this.getAdditionalSurveyInfoForTelemetry(survey, contextInfo, this.getAllCampaignDefinitionIDs(),
				this.getAllCandidateCampaignDefinitionIDs());

			// Only adding the governance service client state at TriggerMet event
			additionalSurveyInfo.governanceServiceClientState = this.orchestrationEngine.getStateForTelemetry();

			TelemetryLogger.getTelemetryLogger().log_TriggerMet(survey.getSurveyInfo().getBackEndId(), survey.getSurveyInfo().getId(),
				survey.getType(), activityTrackingString, JSON.stringify(additionalSurveyInfo), survey.getSurveyInfo().getTreatmentType(), 
				additionalSurveyInfo.isUxSchemaPresent);

			// Set govern channel
			this.orchestrationEngine.startChannelCooldown(governedChannelType);

			const surveyInfo = survey.getSurveyInfo();
			let isSurveyComplete = true;
			if (isBusinessCriticalSurvey(surveyInfo)) {
				// critical campaigns are not tracked
				isSurveyComplete = false;
			} else if (isMultiLaunchSurvey(surveyInfo)) {
				const surveyActivationStats = this.previousSurveyActivationStats.getBySurveyId(surveyInfo.getId());
				isSurveyComplete = hasSurveyReachedMaxLaunches(surveyInfo, surveyActivationStats);
			}

			// Set campaign states
			this.surveyClient.onSurveyActivated(surveyInfo, isSurveyComplete);

			// Show the survey
			this.launchLauncher(survey, contextInfo);

			// check if there are any critical campaigns and start tracking again
			this.setCriticalPendingSurveysAndUpdateActivityListener();
		}
	}

	private async checkTriggerMetApisAndActivateSurvey(survey: ISurvey, contextInfo: IContextData[]): Promise<void> {
		// Get the cooldown setting
		const campaignDefinition = this.surveyClient.getCampaignDefinitions()[survey.getCampaignId()];
		const surveyLevelCooldown = campaignDefinition.nominationScheme.cooldownPeriod.asTimeIntervalSeconds();
		const governedChannelType = survey.getSurveyInfo().getGovernedChannelType();

		// Call the TriggerMet Apis
		let serviceResult: IGovernanceServiceResult;
		try {
			const governanceDecision = await this.queryTriggerMetApisAsync(survey, surveyLevelCooldown, governedChannelType);
			serviceResult = governanceDecision.serviceResult;
		} catch (e) {
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateEngine.QueryTriggerMetApisAsync.Exception,
				{ ErrorMessage: e && e.toString(), ErrorDetails: e && e.stack });
			// failure in case of exception
			serviceResult = {
				permission: false
			};
		}
		let launchSurvey = false;

		// Log the stale
		const governanceResponseTelemetry = JSON.stringify(this.createGorvernanceResponseTelemetry(serviceResult, survey.getCampaignId()));
		TelemetryLogger.getTelemetryLogger().log_Event(
			TelemetryEvent.FloodgateEngine.CheckGovernanceServiceAndActivateSurvey.GovernanceServiceClientCompleted,
			{
				Message: governanceResponseTelemetry,
			});

		// Check if engine is still running
		if (this.initializationStatus !== InitializationStatus.Started) {
			return;
		}

		if (!serviceResult.error) {
			// Check result is permitted
			if (serviceResult.permission) { // the Governance Service is permitting, continue to trigger
				launchSurvey = true;
			} else {
				if (serviceResult.lastTriggerSurvey?.timeUntilNextCampaignAllowed > 0) {
					// Set governed channel to new cooldown
					this.orchestrationEngine.startChannelCooldown(governedChannelType, serviceResult.lastTriggerSurvey.lastTriggerCampaignTime);

					// Clear the survey relating to channel
					this.clearSurveyWithChannel(governedChannelType);
				}

				if (
					serviceResult.isSurveyGoverned &&
					serviceResult.lastTriggerSurvey?.timeUntilSameCampaignAllowed > 0
				) {
					// Log the event
					TelemetryLogger.getTelemetryLogger().log_Event(
						TelemetryEvent.FloodgateEngine.CheckGovernanceServiceAndActivateSurvey.GovernanceServiceSurveyInCooldown,
						{
							Message: governanceResponseTelemetry,
						});
					this.putSurveyOnCooldown(survey, serviceResult);
				}
			}
		} else {
			// If force to display with error, continue to trigger the survey
			if (this.orchestrationEngine.shouldForcePermission()) {
				launchSurvey = true;

				// Log the force to display event
				TelemetryLogger.getTelemetryLogger().log_Event(
					TelemetryEvent.FloodgateEngine.CheckGovernanceServiceAndActivateSurvey.GovernanceServiceForceToDisplay,
					{
						ErrorMessage: serviceResult.errorMessage,
					});
			}
		}

		if (launchSurvey) {
			// Launch the survey
			this.activateSurvey(survey, contextInfo);
			return;
		}

		// If there is only one survey, which is the current one in process, clear queue and return
		if (this.staleTriggeredSurveysQueue.length <= 1) {
			this.staleTriggeredSurveysQueue = [];
			return;
		}

		// Remove the first activated survey from the list which is the current one in process
		this.staleTriggeredSurveysQueue.shift();
		const nextSurvey = this.staleTriggeredSurveysQueue[0].survey;
		const nextContextInfo = this.staleTriggeredSurveysQueue[0].contextInfo;

		// Playback the next activated survey in waiting
		this.checkTriggerMetApisAndActivateSurvey(nextSurvey, nextContextInfo);
	}

	private readString(fileType: FileType): string {
		return this.storage.read(fileType);
	}

	private writeString(fileType: FileType, str: string): void {
		this.storage.write(fileType, str);
	}

	private launchLauncher(survey: ISurvey, contextInfo: IContextData[]): void {
		const launcher: ISurveyLauncher = this.launcherFactory.makeSurveyLauncher(survey);
		if (launcher) {
			this.launchedLaunchers.push(launcher);
			this.onSurveyActivatedCallback.onSurveyActivated(launcher, survey, { contextInfo });
		}
	}

	private clearSurveyWithChannel(governedChannelType: GovernedChannelType) {
		const activeSurveys: IDictionary<ISurvey> = {};

		// Get all the candidate surveys' campaign definition IDs
		for (const key in this.candidateSurveys) {
			if (Object.prototype.hasOwnProperty.call(this.candidateSurveys, key)) {
				const candidateSurvey: ISurvey = this.candidateSurveys[key];
				if (candidateSurvey.getSurveyInfo().getGovernedChannelType() !== governedChannelType) {
					activeSurveys[key] = candidateSurvey;
				}
			}
		}

		this.candidateSurveys = activeSurveys;
		this.activityListener.UpdateActivityTrackingContracts(this.candidateSurveys);
	}

	private createGorvernanceResponseTelemetry(serviceResult: IGovernanceServiceResult, campaignId: string) {
		return {
			CampaignId: campaignId,
			FloodgateEngineStatus: this.initializationStatus.toString(),
			GovernanceServiceResult: serviceResult,
			StaleTriggeredSurveysQueueCount: this.staleTriggeredSurveysQueue.length - 1,
		};
	}

	private putSurveyOnCooldown(survey: ISurvey, serviceResult: IGovernanceServiceResult): void {
		// Set the Survey on cooldown from the value returned from the service
		this.surveyClient.modifyCampaignSurveyTakenDate(
			survey.getCampaignId(),
			serviceResult.lastTriggerSurvey.lastTriggerSameCampaignTime,
			true);
	}

	private async queryTriggerMetApisAsync(
		survey: ISurvey,
		surveyLevelCooldown: number,
		channel: GovernedChannelType): Promise<IGovernanceDecision> {
		const isLoadingSurveyCdnRequired = isTranslationRequired(survey, this.floodgateEngineConfig.locale);
		TelemetryLogger.getTelemetryLogger().log_Event(
			TelemetryEvent.FloodgateEngine.QueryTriggerMetApisAsync.QueryTriggerMetApisAsync,
			{
				Message: JSON.stringify({
					channel,
					IsLoadingSurveyCdnRequired: isLoadingSurveyCdnRequired,
					GovServiceEnabled: this.orchestrationEngine.isChannelGovernanceEnabled(channel),
					FloodgateSurveyCdnClientEnabled: this.floodgateEngineConfig.uiStringsCdnEnabled
				}),
			});

		const governanceParams: IGovernanceParameters = {
			campaignId: survey.getCampaignId(),
			surveyId: survey.getSurveyInfo().getId(),
			surveyType: survey.getType(),
			channelType: channel,
			launcherType: survey.getLauncherType(),
			maxLaunches: survey.getSurveyInfo().getMaxLaunches(),
			campaignCooldown: surveyLevelCooldown,
			userCooldown: -1,
			personalizerEnabled: false
		};

		const businessLevel = survey.getSurveyInfo().getBusinessLevel() as unknown as CampaignBusinessLevel;

		if (isLoadingSurveyCdnRequired && this.floodgateEngineConfig.uiStringsCdnEnabled) {
			const cdnSimpleRequestEnabled = getFeatureGateProvider()?.getBoolFeatureStatus("CdnSimpleRequestEnabled", true);
			const floodgateSurveyCdnClient = new FloodgateSurveyCdnClient(this.floodgateEngineConfig.uiStringsLoadingTimeout,
				this.floodgateEngineConfig.uiStringCdnRetryLimit, cdnSimpleRequestEnabled);
			if (this.floodgateEngineConfig.shouldFetchSurveyCdnConcurrently) {
				const loadCdnPromise = floodgateSurveyCdnClient.loadCdnStringResource(survey, this.floodgateEngineConfig.locale);
				const checkPermission = this.getGovernanceDecision(businessLevel, governanceParams);
				const [cdnLoadResult, mopResult] = await Promise.all([loadCdnPromise, checkPermission]);
				// if SurveyCDN is failed, return false no need to check for MOP result
				if (!cdnLoadResult) {
					TelemetryLogger.getTelemetryLogger().log_Event(
						TelemetryEvent.FloodgateEngine.QueryTriggerMetApisAsync.SurveyCdnFailureConcurrently,
						{
							Message: JSON.stringify({
								cdnLoadResult,
								mopResult
							}),
						});
					return {
						governanceDecision: GovernanceDecision.Rejected
					};
				}
				return mopResult;
			} else {
				const cdnLoadResult = await floodgateSurveyCdnClient.loadCdnStringResource(survey, this.floodgateEngineConfig.locale);
				// if SurveyCDN is failed, return right away, no need to call MOP anymore
				if (!cdnLoadResult) {
					TelemetryLogger.getTelemetryLogger().log_Event(
						TelemetryEvent.FloodgateEngine.QueryTriggerMetApisAsync.SurveyCdnFailureSequentially,
						{
							Message: JSON.stringify({
								cdnLoadResult
							}),
						});
					return {
						governanceDecision: GovernanceDecision.Rejected
					};
				}
				return await this.getGovernanceDecision(businessLevel, governanceParams);
			}
		} else {
			// Current behavior
			return this.getGovernanceDecision(businessLevel, governanceParams);
		}
	}

	private async getGovernanceDecision(businessLevel: CampaignBusinessLevel, governanceParams: IGovernanceParameters): Promise<IGovernanceDecision> {
		if (this.personalizerClient) {
			const isEnabled = this.personalizerClient.isEnabled(governanceParams.surveyType, governanceParams.launcherType, governanceParams.channelType);
			governanceParams.personalizerEnabled = isEnabled;
		}

		const governanceDecision = await this.orchestrationEngine.getDecision(businessLevel, governanceParams);
		if (governanceDecision && this.personalizerClient) {
			this.personalizerClient.onGovernanceServiceResult(governanceParams, governanceDecision.serviceResult);
		}

		return governanceDecision;
	}
}
